import React from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useInView from "../../../../hooks/ui_hooks/useInView";
import homepagePhotoWebp from "../../../../assets/homepage.webp";
import homepagePhotoPng from "../../../../assets/homepage-1040.png";

// Illustrative sample records — not live data.
const REMOVED_RECORDS = [
  {
    value: "123 Oak Street, Denver, CO",
    short: "123 Oak Street",
    source: "People-search site",
  },
  {
    value: "(555) 010-4821",
    short: "(555) 010-4821",
    source: "Data broker profile",
  },
];

// ── A record being struck out and removed ────────────────────────────────────
function RemovedRecordChip({ record, delay, inView, sx }) {
  return (
    <Box
      sx={{
        position: "absolute",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        gap: 0.4,
        px: { xs: 1.2, sm: 1.6 },
        py: { xs: 0.9, sm: 1.1 },
        borderRadius: 2,
        // Opaque rather than a backdrop blur: the chips sit above the rotating
        // rings, so a blurred backdrop would be re-filtered every frame.
        backgroundColor: "rgba(11,18,32,0.92)",
        border: "1px solid rgba(255,255,255,0.10)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.45)",
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(10px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        ...sx,
      }}
    >
      <Box sx={{ position: "relative", alignSelf: "flex-start" }}>
        <Typography
          sx={{
            fontSize: { xs: "0.7rem", sm: "0.8rem" },
            fontWeight: 600,
            whiteSpace: "nowrap",
            color: inView ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.92)",
            transition: `color 0.5s ease ${delay + 0.5}s`,
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        >
          {/* Abbreviated on phones so the chip cannot reach across the photo */}
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            {record.short}
          </Box>
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {record.value}
          </Box>
        </Typography>

        {/* The strike sweeps across once the chip has settled */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "52%",
            height: "1.5px",
            backgroundColor: "#12b2f1",
            transformOrigin: "left center",
            transform: inView ? "scaleX(1)" : "scaleX(0)",
            transition: `transform 0.55s cubic-bezier(0.22,1,0.36,1) ${delay + 0.4}s`,
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            backgroundColor: "#12b2f1",
            flexShrink: 0,
          }}
        />
        <Typography
          sx={{
            fontSize: { xs: "0.58rem", sm: "0.66rem" },
            fontWeight: 700,
            letterSpacing: "0.07em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            color: "rgba(255,255,255,0.55)",
          }}
        >
          Removed
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            {" \u00b7 "}
            {record.source}
          </Box>
        </Typography>
      </Box>
    </Box>
  );
}

// ── Orbit rings, carried over from the previous shield treatment ────────────
function OrbitRings({ inView, isVisible }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 400 400"
      aria-hidden
      focusable="false"
      sx={{
        position: "absolute",
        left: "52%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        width: "82%",
        height: "auto",
        pointerEvents: "none",
        opacity: inView ? 1 : 0,
        transition: "opacity 0.9s ease 0.15s",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <defs>
        <style>{`
          @keyframes why-spin { to { transform: rotate(360deg); } }
          @keyframes why-spin-reverse { to { transform: rotate(-360deg); } }
          @keyframes why-pulse { 0%, 100% { opacity: 0.15; } 50% { opacity: 0.35; } }
          .why-ring-outer {
            transform-origin: 200px 200px;
            animation: why-spin 22s linear infinite;
          }
          .why-ring-inner {
            transform-origin: 200px 200px;
            animation: why-spin-reverse 16s linear infinite;
          }
          .why-pulse { animation: why-pulse 3s ease-in-out infinite; }
          @media (prefers-reduced-motion: reduce) {
            .why-ring-outer, .why-ring-inner, .why-pulse { animation: none; }
          }
        `}</style>
      </defs>

      {/* Faint outermost pulse ring */}
      <circle
        cx="200"
        cy="200"
        r="196"
        stroke="rgba(18,178,241,0.12)"
        strokeWidth="1"
        fill="none"
        className="why-pulse"
        style={{ animationPlayState: isVisible ? "running" : "paused" }}
      />

      {/* Outer dashed orbit */}
      <g
        className="why-ring-outer"
        style={{ animationPlayState: isVisible ? "running" : "paused" }}
      >
        <circle
          cx="200"
          cy="200"
          r="178"
          stroke="rgba(18,178,241,0.2)"
          strokeWidth="1.5"
          strokeDasharray="6 10"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="200" cy="22" r="4" fill="#12b2f1" opacity="0.7" />
      </g>

      {/* Inner dashed orbit, counter-rotating */}
      <g
        className="why-ring-inner"
        style={{ animationPlayState: isVisible ? "running" : "paused" }}
      >
        <circle
          cx="200"
          cy="200"
          r="158"
          stroke="rgba(18,178,241,0.15)"
          strokeWidth="1"
          strokeDasharray="4 14"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="284" cy="66" r="3" fill="#12b2f1" opacity="0.5" />
      </g>

      {/* Static status dots */}
      <circle cx="378" cy="200" r="3.5" fill="#12b2f1" opacity="0.5" />
      <circle cx="200" cy="378" r="3.5" fill="#12b2f1" opacity="0.3" />
      <circle cx="22" cy="200" r="3.5" fill="#12b2f1" opacity="0.5" />
    </Box>
  );
}

// ── Cut-out subject with records being scrubbed off around them ─────────────
function RemovalVisual() {
  const [ref, hasEntered, isVisible] = useInView();

  return (
    <Box
      ref={ref}
      sx={{ position: "relative", width: "100%", maxWidth: 520, mx: "auto" }}
    >
      {/* Ambient brand glow seats the cut-out so it isn't floating in a void */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-10%",
          pointerEvents: "none",
          background:
            "radial-gradient(ellipse at 52% 55%, rgba(18,178,241,0.18), rgba(18,178,241,0) 62%)",
          filter: "blur(36px)",
        }}
      />

      <OrbitRings inView={hasEntered} isVisible={isVisible} />

      {/* The PNG is a transparent cut-out, so it gets no card, border or
          shadow — any of those would draw a rectangle the subject lacks.
          Sitting above the rings, her silhouette occludes them. */}
      <Box
        component="picture"
        sx={{
          position: "relative",
          display: "block",
          opacity: hasEntered ? 1 : 0,
          transform: hasEntered ? "translateY(0)" : "translateY(16px)",
          transition:
            "opacity 0.7s ease, transform 0.7s cubic-bezier(0.22,1,0.36,1)",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }}
      >
        <source srcSet={homepagePhotoWebp} type="image/webp" />
        <Box
          component="img"
          src={homepagePhotoPng}
          alt="A person working on a laptop at their desk"
          loading="lazy"
          decoding="async"
          sx={{
            display: "block",
            width: "100%",
            height: "auto",
            aspectRatio: "3 / 2",
          }}
        />
      </Box>

      <RemovedRecordChip
        record={REMOVED_RECORDS[0]}
        delay={0.25}
        inView={hasEntered}
        sx={{ top: { xs: "2%", sm: "8%" }, left: { xs: 0, sm: "-4%" } }}
      />

      <RemovedRecordChip
        record={REMOVED_RECORDS[1]}
        delay={0.85}
        inView={hasEntered}
        sx={{ bottom: { xs: "6%", sm: "10%" }, right: { xs: 0, sm: "-2%" } }}
      />
    </Box>
  );
}

// ── Why component ─────────────────────────────────────────────────────────────
const Why = () => {
  const navigate = useNavigate();

  const handleNavigate = (e) => {
    e.currentTarget.blur();
    navigate("/pricing");
  };

  return (
    <Box sx={{ width: "100%", backgroundColor: "#131a34", overflow: "hidden" }}>
      <Box
        component="section"
        sx={{
          py: { xs: 6, sm: 8, md: 10 },
          pb: { xs: 14, sm: 16 },
          position: "relative",
          maxWidth: 1200,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          {/* LEFT CONTENT */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography
                sx={{
                  color: "#F59E0B",
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontSize: "0.85rem",
                  mb: 1.5,
                }}
              >
                Online Privacy Protection
              </Typography>

              <Typography
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2,
                  letterSpacing: "-0.015em",
                  fontSize: { xs: "2.8rem", sm: "2.8rem", md: "3.3rem" },
                  color: "white",
                }}
              >
                Your{" "}
                <Box
                  component="span"
                  sx={{ color: "#12b2f1", fontWeight: 700 }}
                >
                  Personal Data
                </Box>{" "}
                is exposed on the web
              </Typography>

              <Typography
                sx={{
                  mt: 2,
                  color: "rgba(255,255,255,0.78)",
                  maxWidth: 720,
                  fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                  lineHeight: 1.6,
                  fontWeight: 400,
                }}
              >
                We build tools that put you back in control by making data
                removal and tracking simple, visible, and reliable.
              </Typography>

              {/* Desktop Button */}
              <Button
                variant="contained"
                size="large"
                onClick={handleNavigate}
                sx={{
                  mt: 4,
                  backgroundColor: "#F59E0B",
                  color: "#0b1220",
                  fontWeight: 900,
                  textTransform: "none",
                  borderRadius: 1,
                  px: 4,
                  "&:hover": { backgroundColor: "#D97706" },
                  "&:focus": { backgroundColor: "#F59E0B" },
                  "&:focus-visible": { backgroundColor: "#F59E0B" },
                  display: { xs: "none", lg: "inline-flex" },
                }}
              >
                Sign Up
              </Button>
            </Box>
          </Grid>

          {/* RIGHT VISUAL */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: { xs: 6, sm: 8 },
              }}
            >
              <RemovalVisual />
            </Box>
          </Grid>
        </Grid>

        {/* Mobile CTA */}
        <Box
          sx={{
            display: { xs: "flex", lg: "none" },
            justifyContent: "center",
            mt: 4,
          }}
        >
          {/* Mobile Button */}
          <Button
            fullWidth
            onClick={handleNavigate}
            sx={{
              backgroundColor: "#F59E0B",
              color: "#0b1220",
              fontWeight: 900,
              borderRadius: 1.5,
              maxWidth: 420,
              textTransform: "none",
              px: 4,
              py: 1.5,
              "&:hover": { backgroundColor: "#D97706" },
              "&:focus": { backgroundColor: "#F59E0B" },
              "&:focus-visible": { backgroundColor: "#F59E0B" },
            }}
          >
            Sign Up
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Why;
